import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";

// Sentry: инициализира се само в production среда (DSN + environment="production").
// Конфигурацията идва от runtime /config.js (window.__ENV), а build-time fallback е през Vite.
const sentryEnv =
  (typeof window !== "undefined" && window.__ENV?.VITE_SENTRY_ENVIRONMENT) ||
  import.meta.env.VITE_SENTRY_ENVIRONMENT ||
  "";
const sentryDsn =
  (typeof window !== "undefined" && window.__ENV?.VITE_SENTRY_DSN) ||
  import.meta.env.VITE_SENTRY_DSN ||
  "";
if (sentryDsn && sentryEnv === "production") {
  const appVersion =
    (typeof window !== "undefined" && window.__ENV?.VITE_APP_VERSION) ||
    import.meta.env.VITE_APP_VERSION ||
    undefined;
  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnv,
    release: appVersion,
    sendDefaultPii: true,
  });
}


import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";

// Всички antd DatePicker-и четат първия ден на седмицата от dayjs локала —
// без това календарите започват от неделя.
dayjs.extend(updateLocale);
dayjs.updateLocale("en", { weekStart: 1 });

import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider, QueryCache } from "@tanstack/react-query";
import { Provider } from "react-redux";
import { App } from "antd";
import { MessageBridge, showGlobalErrorToast } from "./lib/messageBridge";
import UserConfigurationSync from "./components/UserConfigurationSync";
import ThemedConfig from "./components/ThemedConfig";
import { store } from "./store/store";
import { fetchUserInfo, hydrateUserInfo } from "./store/authSlice";
import { getToken, getAccountIsPersonal } from "./lib/auth";
import { readCachedUserInfo } from "./lib/userInfoCache";
import { isEdgeFallbackEnabledFromCache } from "./lib/edge/edgeConfig";
import { setStoreRef } from "./api/client";
import { getRestaurantName } from "./lib/restaurant";
import { registerAppServiceWorker } from "./lib/serviceWorker";
import { initEdge } from "./lib/edge/edgeMode";

document.title = `UnrealSoft - ${getRestaurantName()}`;

// Регистрирай store референция за axios interceptor
// Това дава на axios interceptor-а достъп до Redux store-а, за да може да чете текущия токен
setStoreRef(store);

// Service worker (src/sw.ts → /sw.js, docs/pwa.md): офлайн кеш на самото
// приложение + Web Push. Идемпотентна регистрация при старт, за да получава
// вече включено устройство известия и след reload. Разрешения/абонаменти се
// пипат само от жест (модалът „Известия").
registerAppServiceWorker();

// Зареждаме user info на старт само за личен акаунт. При акаунт за общ достъп
// (`account_is_personal === false`) изчакваме пина — служителските роли се
// зареждат след въвеждане на код в SharedAccountGate; иначе при refresh бихме
// ползвали споделения токен преди пина.
// Първо кешът на самоличността (lib/userInfoCache.ts, docs/auth-access.md):
// последният успешен /user/info за този токен хидратира Redux ВЕДНАГА, така
// че route guard-овете не отскачат към „Модули" без интернет, а живият
// fetchUserInfo само опреснява на заден план. Хидратира се САМО при включен
// резерв (кешираният `edge_config.enabled`) — иначе стартът е точно както
// преди офлайн режима.
if (getToken() && getAccountIsPersonal() !== false) {
  const cachedUserInfo = isEdgeFallbackEnabledFromCache()
    ? readCachedUserInfo(getToken())
    : null;
  if (cachedUserInfo) store.dispatch(hydrateUserInfo(cachedUserInfo));
  store.dispatch(fetchUserInfo());
}
import AuthorizedRoute from "./routes/AuthorizedRoute";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
// antd reset-ът се импортира от index.css в най-нисък CSS layer (antd-reset),
// за да НЕ бие Tailwind utility класовете (не-layered CSS печели срещу layered).
import "./index.css";
import "./translations/i18n";

import ProtectedRoute from "./routes/ProtectedRoute";

import LoginPage from "./pages/Login";
import Modules from "./pages/modules/Modules";
import OrderingWithCode from "./pages/ordering/OrderingWithCode";
import LunchMenuPage from "./pages/ordering/lunch/LunchMenuPage";
import Configuration from "./pages/configuration/Configuration";
import Monitors from "./pages/monitors/Monitors";
import Reports from "./pages/reports/Reports";
import ReservationsLayout from "./pages/reservations/ReservationsLayout";
import Reservations from "./pages/reservations/Reservations";
import ReservationsTable from "./pages/reservations/table/ReservationsTable";
import Inventory from "./pages/inventory/Inventory";
import { DEFAULT_INVENTORY_SLUG } from "./pages/inventory/components/shell/inventoryNav";
import MonitorsPage from "./pages/configuration/components/monitors/MonitorsPage";
import DevicesPage from "./pages/configuration/components/devices/DevicesPage";
import OrderingSettingsPage from "./pages/configuration/components/ordering-settings/OrderingSettingsPage";
import WorkingHoursPage from "./pages/configuration/components/working-hours/WorkingHoursPage";
import PaymentMethodsPage from "./pages/configuration/components/payment-methods/PaymentMethodsPage";
import CompaniesPage from "./pages/configuration/components/companies/CompaniesPage";
import RestaurantSchemaPage from "./pages/reservations/restaurant-schema/RestaurantSchemaPage";
import TablesConfigPage from "./pages/reservations/tables-config/TablesConfigPage";
import Discounts from "./pages/discounts/Discounts";
import Delivery from "./pages/delivery/Delivery";
import Changelog from "./pages/changelog/Changelog";
import AuditLog from "./pages/monitoring/auditLog/AuditLog";
import Monitoring from "./pages/monitoring/Monitoring";
import MonitoringFiscalIssues from "./pages/monitoring/fiscalIssues/FiscalIssuesPage";
import MonitoringAssistant from "./pages/monitoring/assistant/AssistantPage";
import InstallBanner from "./components/InstallBanner";
import VersionCheckNotifier from "./components/VersionCheckNotifier";

// Глобален error handler за useQuery: показваме toast само за 403,
// защото мутациите вече се обработват в техните catch блокове.
const queryCache = new QueryCache();
queryCache.subscribe((event) => {
  if (event.type !== "updated") return;
  // В @tanstack/react-query v5 неуспешен fetch се dispatch-ва като action.type === "failed".
  // Грешката е в самата query state (query.state.error), не в action-а.
  if (event.action.type !== "failed") return;
  // Action-ът съдържа самата грешка като action.error.
  const error = (event.action as { error?: unknown }).error as {
    response?: { status?: number; data?: { detail?: string } };
  } | undefined;
  if (error?.response?.status === 403) {
    const detail = error.response?.data?.detail;
    if (typeof detail === "string" && detail) {
      showGlobalErrorToast(detail);
    }
  }
});
const queryClient = new QueryClient({
  queryCache,
  // Поведението по подразбиране на react-query е писано за здрава мрежа, а
  // терминалите работят на ресторантски Wi-Fi (docs/edge.md):
  //
  //  • `retry: 3` с нарастващо изчакване (1/2/4 s) значи ЧЕТИРИКРАТЕН товар
  //    точно когато мрежата е болна, и „вечно зареждане" за човека отсреща.
  //    Един повторен опит хваща единичното препъване; повече е шум;
  //  • мутациите НЕ се повтарят изобщо: закриване на сметка, повторено от
  //    само себе си, е втори фискален бон;
  //  • `networkMode: "online"` паузира заявките по `navigator.onLine`, който
  //    при авария на доставчика си остава `true` (Wi-Fi-ът е вдигнат), а при
  //    изключен Wi-Fi блокира `mutateAsync` ЗАВИНАГИ — обещанието не се
  //    решава и екранът виси. Режимът облак/каса се решава от сондите, не от
  //    флага на браузъра, затова тук е `"always"`.
  defaultOptions: {
    queries: { retry: 1, retryDelay: 500, networkMode: "always" },
    mutations: { retry: 0, networkMode: "always" },
  },
});

// Локален резерв (docs/edge.md): режимът облак/каса трябва да е известен на
// axios интерсептора преди първия рендер — инициализира се тук, извън React.
initEdge(store, queryClient);

// При смяна на влезлия потребител ИЛИ на ограничения достъп (access) кешът на
// заявките се изпразва ИЗЦЯЛО: отговорите в него са давани при другите права и
// не бива да „светват" със старите данни, докато тече refetch-ът (напр.
// нефискализирани обороти в Справки след „Изход от ограничен достъп", или
// данните на предишния служител след смяна с код на общ терминал).
let lastQueryScope = `${store.getState().auth.userId ?? ""}|${store.getState().auth.access}`;
store.subscribe(() => {
  const auth = store.getState().auth;
  const queryScope = `${auth.userId ?? ""}|${auth.access}`;
  if (queryScope === lastQueryScope) return;
  lastQueryScope = queryScope;
  queryClient.removeQueries();
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <ThemedConfig>
        <App message={{ duration: 3, maxCount: 3, top: 24 }}>
          <MessageBridge />
          <UserConfigurationSync />
          <VersionCheckNotifier />
          <BrowserRouter>
            {/* PWA Install Banner — показва се само на /login (преди логин) */}
            <InstallBanner />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Modules />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ordering"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredRoleName="ordering">
                      <OrderingWithCode />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              />
              {/* Обедно меню — отваря се от „Опции" на масите (docs/ordering.md) */}
              <Route
                path="/ordering/lunch-menu"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredPermissionName="manage_lunch_menu">
                      <LunchMenuPage />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/configuration"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredRoleName="configuration">
                      <Configuration />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              >
                {/* index shows the basic configuration landing */}
                <Route
                  index
                  element={
                    <AuthorizedRoute requiredPermissionName="manage_monitors">
                      <MonitorsPage />
                    </AuthorizedRoute>
                  }
                />
                <Route
                  path="devices"
                  element={
                    <AuthorizedRoute requiredPermissionName="manage_devices">
                      <DevicesPage />
                    </AuthorizedRoute>
                  }
                />
                <Route
                  path="ordering"
                  element={
                    <AuthorizedRoute requiredPermissionName="ordering-settings">
                      <OrderingSettingsPage />
                    </AuthorizedRoute>
                  }
                />
                <Route
                  path="working-hours"
                  element={
                    <AuthorizedRoute requiredPermissionName="ordering-settings">
                      <WorkingHoursPage />
                    </AuthorizedRoute>
                  }
                />
                {/* Старият адрес на страницата (до 0.0.234) — пренасочва, за да
                    не умрат запазени линкове. */}
                <Route
                  path="non-fiscal-display"
                  element={<Navigate to="/configuration/ordering" replace />}
                />
                <Route
                  path="payment-methods"
                  element={
                    <AuthorizedRoute requiredPermissionName="manage_payment_types">
                      <PaymentMethodsPage />
                    </AuthorizedRoute>
                  }
                />
                <Route
                  path="companies"
                  element={
                    <AuthorizedRoute requiredPermissionName="manage_companies">
                      <CompaniesPage />
                    </AuthorizedRoute>
                  }
                />
              </Route>
              <Route
                path="/monitors"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredRoleName="monitors">
                      <Monitors />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              />
              {/* /reports без страница → каноничната първа справка */}
              <Route
                path="/reports"
                element={<Navigate to="/reports/dashboard" replace />}
              />
              {/* Всяка справка е истинска страница със собствен URL (както
                  /inventory/:page). Reports остава монтиран при смяна на
                  :page, а датата живее в query параметъра ?date=. */}
              <Route
                path="/reports/:page"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredRoleName="reports">
                      <Reports />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              />
              {/* Модулът със страничното меню (като „Конфигурация"): всяка страница по право. */}
              <Route
                path="/reservations"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredRoleName="reservations">
                      <ReservationsLayout />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              >
                {/* Схемата на деня (списък, настаняване) */}
                <Route
                  index
                  element={
                    <AuthorizedRoute requiredPermissionName="view_reservations">
                      <Reservations />
                    </AuthorizedRoute>
                  }
                />
                <Route
                  path="table"
                  element={
                    <AuthorizedRoute requiredPermissionName="view_reservations">
                      <ReservationsTable />
                    </AuthorizedRoute>
                  }
                />
                <Route
                  path="restaurant-schema"
                  element={
                    <AuthorizedRoute requiredPermissionName="manage_restaurant_schema">
                      <RestaurantSchemaPage />
                    </AuthorizedRoute>
                  }
                />
                <Route
                  path="tables"
                  element={
                    <AuthorizedRoute requiredPermissionName="manage_tables">
                      <TablesConfigPage />
                    </AuthorizedRoute>
                  }
                />
              </Route>
              {/* /inventory без страница → началната страница на модула (таблото) */}
              <Route
                path="/inventory"
                element={<Navigate to={`/inventory/${DEFAULT_INVENTORY_SLUG}`} replace />}
              />
              {/* Работни листове (доставка/изписване — създаване/редакция) —
                  цели страници вътре в Inventory layout-а (sidebar-ът остава).
                  Преди /inventory/:page, за да не се хванат от него. */}
              <Route
                path="/inventory/delivery/new"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredRoleName="inventory" requiresAccess>
                      <Inventory worksheet={{ page: "delivery", mode: "new" }} />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory/delivery/:deliveryId/edit"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredRoleName="inventory" requiresAccess>
                      <Inventory
                        worksheet={{ page: "delivery", mode: "edit" }}
                      />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory/writeoff/new"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredRoleName="inventory" requiresAccess>
                      <Inventory worksheet={{ page: "writeoff", mode: "new" }} />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory/writeoff/:writeoffId/edit"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredRoleName="inventory" requiresAccess>
                      <Inventory
                        worksheet={{ page: "writeoff", mode: "edit" }}
                      />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory/stocktake/new"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredRoleName="inventory" requiresAccess>
                      <Inventory
                        worksheet={{ page: "stocktake", mode: "new" }}
                      />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory/stocktake/:stocktakeId/edit"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredRoleName="inventory" requiresAccess>
                      <Inventory
                        worksheet={{ page: "stocktake", mode: "edit" }}
                      />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory/transfer/new"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredRoleName="inventory" requiresAccess>
                      <Inventory worksheet={{ page: "transfer", mode: "new" }} />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory/transfer/:transferId/edit"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredRoleName="inventory" requiresAccess>
                      <Inventory
                        worksheet={{ page: "transfer", mode: "edit" }}
                      />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory/interchangeables/new"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredRoleName="inventory" requiresAccess>
                      <Inventory
                        worksheet={{ page: "interchangeables", mode: "new" }}
                      />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory/interchangeables/:groupId/edit"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredRoleName="inventory" requiresAccess>
                      <Inventory
                        worksheet={{ page: "interchangeables", mode: "edit" }}
                      />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              />
              {/* Старите адреси на разнасянето: изборът на отчет вече е в
                  самата страница, а корекцията е „анулирай и разнеси
                  повторно" от справката „Разнесени отчети". */}
              <Route
                path="/inventory/posting/new"
                element={<Navigate to="/inventory/posting" replace />}
              />
              <Route
                path="/inventory/posting/:postingId/edit"
                element={<Navigate to="/inventory/posted-reports" replace />}
              />
              {/* Всяка складова страница е истинска страница със собствен URL.
                  Inventory остава монтиран при смяна на :page (само параметърът
                  се променя), за да се запази избраният склад. */}
              <Route
                path="/inventory/:page"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredRoleName="inventory" requiresAccess>
                      <Inventory />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/discounts"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredRoleName="discounts">
                      <Discounts />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/delivery"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredRoleName="delivery">
                      <Delivery />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/changelog"
                element={
                  <ProtectedRoute>
                    <AuthorizedRoute requiredRoleName="reports">
                      <Changelog />
                    </AuthorizedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/audit-log"
                element={
                  <ProtectedRoute>
                    <AuditLog />
                  </ProtectedRoute>
                }
              />
              {/* Мониторинг — служебен модул само за user id = 1; гейтът по
                  userId е вътре в компонентите (както при /audit-log). */}
              <Route
                path="/monitoring"
                element={
                  <ProtectedRoute>
                    <Monitoring />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/monitoring/fiscal-issues"
                element={
                  <ProtectedRoute>
                    <MonitoringFiscalIssues />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/monitoring/assistant"
                element={
                  <ProtectedRoute>
                    <MonitoringAssistant />
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<Navigate to="/" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </App>
        </ThemedConfig>
      </Provider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </React.StrictMode>,
);
